import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { db } from "@/lib/firebase";
import { collection, query, where, getDocs } from "firebase/firestore";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CalendarSyncButton, VisualCalendar, AvailabilitySettings } from "@/components/calendar";
import { Calendar, Clock, Loader2 } from "lucide-react";
import { Meeting } from "@/types/calendar.types";

const translations = {
  en: {
    title: "Calendar",
    subtitle: "Manage your schedule and availability",
    myCalendar: "My Calendar",
    availability: "Availability",
    sync: "Sync",
    syncDescription: "Connect your external calendars",
    availabilityDescription: "Set your weekly working hours",
    calendarDescription: "View and manage your meetings",
    loading: "Loading meetings..."
  },
  fr: {
    title: "Calendrier",
    subtitle: "Gérez votre emploi du temps et vos disponibilités",
    myCalendar: "Mon calendrier",
    availability: "Disponibilités",
    sync: "Synchronisation",
    syncDescription: "Connectez vos calendriers externes",
    availabilityDescription: "Définissez vos heures de travail hebdomadaires",
    calendarDescription: "Visualisez et gérez vos réunions",
    loading: "Chargement des réunions..."
  }
};

interface CalendarTabProps {
  showAvailabilitySettings?: boolean;
}

export function CalendarTab({ showAvailabilitySettings = true }: CalendarTabProps) {
  const { user } = useAuth();
  const { language } = useLanguage();
  const t = translations[language as keyof typeof translations] || translations.en;
  const [activeSubTab, setActiveSubTab] = useState("calendar");
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadMeetings();
  }, [user]);

  const loadMeetings = async () => {
    if (!user?.email) {
      setIsLoading(false);
      return;
    }

    try {
      // Try loading from calendarEvents collection first
      const eventsRef = collection(db, "calendarEvents");
      const eventsQuery = query(
        eventsRef,
        where("createdBy", "==", user.email)
      );
      
      const snapshot = await getDocs(eventsQuery);
      const meetingsData: Meeting[] = snapshot.docs.map(doc => {
        const data = doc.data();
        const startTime = data.startTime?.toDate?.() || new Date(data.startTime);
        const endTime = data.endTime?.toDate?.() || new Date(data.endTime);
        const durationMinutes = Math.round((endTime.getTime() - startTime.getTime()) / 60000);
        
        return {
          id: doc.id,
          title: data.title || 'Untitled Meeting',
          description: data.description,
          organizerId: user.email || '',
          participants: data.attendees?.map((a: any) => a.email) || [],
          date: startTime.toISOString().split('T')[0],
          time: startTime.toTimeString().slice(0, 5),
          duration: durationMinutes,
          status: (data.status || 'confirmed') as Meeting['status'],
          type: 'internal' as Meeting['type'],
          meetingUrl: data.meetingLink,
          createdAt: data.createdAt?.toDate?.() || new Date(),
          dealId: data.dealId,
        };
      });
      
      setMeetings(meetingsData);
    } catch (error) {
      console.error("Error loading meetings:", error);
      setMeetings([]);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        <span className="ml-2 text-muted-foreground">{t.loading}</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Calendar className="h-6 w-6" />
            {t.title}
          </h2>
          <p className="text-muted-foreground">{t.subtitle}</p>
        </div>
        <CalendarSyncButton />
      </div>

      <Tabs value={activeSubTab} onValueChange={setActiveSubTab}>
        <TabsList>
          <TabsTrigger value="calendar" className="gap-2">
            <Calendar className="h-4 w-4" />
            {t.myCalendar}
          </TabsTrigger>
          {showAvailabilitySettings && (
            <TabsTrigger value="availability" className="gap-2">
              <Clock className="h-4 w-4" />
              {t.availability}
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="calendar" className="space-y-6 mt-6">
          <Card>
            <CardHeader>
              <CardTitle>{t.myCalendar}</CardTitle>
              <CardDescription>{t.calendarDescription}</CardDescription>
            </CardHeader>
            <CardContent>
              <VisualCalendar meetings={meetings} />
            </CardContent>
          </Card>
        </TabsContent>

        {showAvailabilitySettings && (
          <TabsContent value="availability" className="space-y-6 mt-6">
            <AvailabilitySettings />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
